import { memo } from 'react';

export interface FilterChipItem {
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
  readonly count?: number;
  readonly disabled?: boolean;
}

export interface FilterChipsProps {
  readonly chips: readonly FilterChipItem[];
  readonly onToggle: (id: string) => void;
  readonly ariaLabel: string;
  /** toggle => aria-pressed button contract; tab => aria-selected tab contract */
  readonly semantics?: 'toggle' | 'tab';
  readonly className?: string;
  readonly testId?: string;
}

export const FilterChips = memo<FilterChipsProps>(({
  chips,
  onToggle,
  ariaLabel,
  semantics = 'toggle',
  className,
  testId = 'filter-chips',
}) => {
  if (chips.length === 0) {
    return null;
  }

  return (
    <div
      className={['flex min-w-0 max-w-full flex-wrap gap-2', className].filter(Boolean).join(' ')}
      role={semantics === 'tab' ? 'tablist' : 'group'}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {chips.map((chip) => {
        const baseClass =
          'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium';
        const selectedClass = chip.selected
          ? 'border-[var(--color-an-primary)] bg-[var(--color-an-primary-soft)] text-[var(--color-an-primary)]'
          : 'border-[var(--color-an-border)] bg-[var(--color-an-surface)] text-[var(--color-an-text-muted)]';
        return (
          <button
            key={chip.id}
            type="button"
            role={semantics === 'tab' ? 'tab' : undefined}
            aria-selected={semantics === 'tab' ? chip.selected : undefined}
            aria-pressed={semantics === 'toggle' ? chip.selected : undefined}
            tabIndex={semantics === 'tab' && !chip.selected ? -1 : undefined}
            disabled={chip.disabled}
            className={[baseClass, selectedClass, 'disabled:opacity-60'].join(' ')}
            data-testid={`${testId}-chip-${chip.id}`}
            onClick={() => onToggle(chip.id)}
          >
            <span className="truncate">{chip.label}</span>
            {typeof chip.count === 'number' ? (
              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-md bg-[var(--color-an-bg-elevated)] px-1.5 text-xs tabular-nums">
                {chip.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
});

FilterChips.displayName = 'FilterChips';
